
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TablePagination } from "@/components/ui/table-pagination";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { History, Clock, TrendingUp, TrendingDown, BarChart3, Search, Trash2, Eye, Download, FileText, Image, ChevronDown } from "lucide-react";
import { useAnalysisHistory } from '@/hooks/useAnalysisHistory';
import { AiAnalysisSession } from '@/services/AiMarketAnalysisService';
import { MarketAnalysisExportService } from '@/services/MarketAnalysisExportService';
import { ScreenshotExportService } from '@/services/ScreenshotExportService';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';

const MarketAnalysisHistoryPage = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const { sessions, isLoading, error, deleteSession } = useAnalysisHistory({
    symbol: searchTerm,
    status: statusFilter === 'all' ? '' : statusFilter
  });

  // Pagination logic
  const totalItems = sessions.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedSessions = sessions.slice(startIndex, endIndex);

  // Filter completed sessions for export
  const completedSessions = sessions.filter(session => session.status === 'COMPLETED');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-700 border-green-200';
      case 'PROCESSING': 
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'FAILED': return 'bg-red-100 text-red-700 border-red-200';
      case 'RATE_LIMITED': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'BUY': return 'text-green-600 bg-green-100 border-green-200';
      case 'SELL': return 'text-red-600 bg-red-100 border-red-200';
      case 'HOLD': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'BUY': return <TrendingUp className="h-4 w-4" />;
      case 'SELL': return <TrendingDown className="h-4 w-4" />;
      default: return <BarChart3 className="h-4 w-4" />;
    }
  };

  const getRecommendationAction = (session: AiAnalysisSession): string => {
    try {
      const aiRecommendation = session.card_results?.ai_recommendation;
      if (aiRecommendation?.status === 'completed' && aiRecommendation?.data) {
        return aiRecommendation.data.action || 'HOLD';
      }
    } catch (error) {
      console.error('Error parsing AI recommendation:', error);
    }
    return 'HOLD';
  };

  const handleViewDetails = (sessionId: string) => {
    navigate(`/market-analysis/history/${sessionId}`);
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (window.confirm('Are you sure you want to delete this analysis session?')) {
      const result = await deleteSession(sessionId);
      if (result.success) {
        toast.success('Analysis session deleted successfully');
      } else {
        toast.error(result.error || 'Failed to delete analysis session');
      }
    }
  };

  const handleExportAllPDF = async () => {
    if (completedSessions.length === 0) {
      toast.error('No completed analyses available for export');
      return;
    }

    setIsExporting(true);
    try {
      await MarketAnalysisExportService.exportMultipleAnalyses(completedSessions);
      toast.success(`Exported ${completedSessions.length} analyses as PDF successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export analyses as PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllScreenshots = async () => {
    if (completedSessions.length === 0) {
      toast.error('No completed analyses available for export');
      return;
    }

    setIsExporting(true);
    try {
      toast.info('Starting bulk screenshot export. This may take a while...');
      await ScreenshotExportService.exportMultipleAnalysesAsScreenshots(completedSessions);
      toast.success(`Exported ${completedSessions.length} analyses as images successfully`);
    } catch (error) {
      console.error('Screenshot export error:', error);
      toast.error('Failed to export analyses as screenshots');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportSelectedPDF = async () => {
    if (selectedSessions.length === 0) {
      toast.error('Please select at least one analysis to export');
      return;
    }

    setIsExporting(true);
    try {
      const sessionsToExport = sessions.filter(session => 
        selectedSessions.includes(session.id) && session.status === 'COMPLETED'
      );
      
      if (sessionsToExport.length === 0) {
        toast.error('No completed analyses selected for export');
        return;
      }

      await MarketAnalysisExportService.exportMultipleAnalyses(sessionsToExport);
      toast.success(`Exported ${sessionsToExport.length} analyses as PDF successfully`);
      setSelectedSessions([]);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export analyses as PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportSelectedScreenshots = async () => {
    if (selectedSessions.length === 0) {
      toast.error('Please select at least one analysis to export');
      return;
    }

    setIsExporting(true);
    try {
      const sessionsToExport = sessions.filter(session => 
        selectedSessions.includes(session.id) && session.status === 'COMPLETED'
      );
      
      if (sessionsToExport.length === 0) {
        toast.error('No completed analyses selected for export');
        return;
      }

      toast.info('Starting selected screenshot export...');
      await ScreenshotExportService.exportMultipleAnalysesAsScreenshots(sessionsToExport);
      toast.success(`Exported ${sessionsToExport.length} analyses as images successfully`);
      setSelectedSessions([]);
    } catch (error) {
      console.error('Screenshot export error:', error);
      toast.error('Failed to export analyses as screenshots');
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-red-500 mb-4">Error loading analysis history</div>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">
        {/* Header - Mobile Optimized */}
        <div className="text-center mb-6 sm:mb-10 animate-fade-in">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500/20 via-blue-500/10 to-transparent border border-blue-500/20 backdrop-blur-sm">
              <History className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-foreground via-foreground to-blue-600 bg-clip-text text-transparent">
              Analysis History
            </h1>
          </div>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto px-4">
            Review your past market analysis results and AI recommendations
          </p>
        </div>

        {/* Export Actions - Mobile Optimized */}
        {completedSessions.length > 0 && (
          <Card className="bg-gradient-to-br from-background via-background to-muted/20 border border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Download className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                Export Options
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      disabled={isExporting}
                      className={`flex items-center gap-2 ${isMobile ? 'w-full justify-center min-h-[44px]' : ''}`}
                      size={isMobile ? "lg" : "default"}
                    >
                      <Download className="h-4 w-4" />
                      {isExporting ? 'Exporting...' : `Export All (${completedSessions.length})`}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-background border shadow-lg">
                    <DropdownMenuItem onClick={handleExportAllPDF} className="cursor-pointer">
                      <FileText className="h-4 w-4 mr-2" />
                      Export All as PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportAllScreenshots} className="cursor-pointer">
                      <Image className="h-4 w-4 mr-2" />
                      Export All as Images
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {selectedSessions.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        disabled={isExporting}
                        variant="outline"
                        className={`flex items-center gap-2 ${isMobile ? 'w-full justify-center min-h-[44px]' : ''}`}
                        size={isMobile ? "lg" : "default"}
                      >
                        <Download className="h-4 w-4" />
                        Export Selected ({selectedSessions.length})
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 bg-background border shadow-lg">
                      <DropdownMenuItem onClick={handleExportSelectedPDF} className="cursor-pointer">
                        <FileText className="h-4 w-4 mr-2" />
                        Export Selected as PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleExportSelectedScreenshots} className="cursor-pointer">
                        <Image className="h-4 w-4 mr-2" />
                        Export Selected as Images
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                
                <div className="text-xs sm:text-sm text-muted-foreground flex items-center justify-center sm:justify-start">
                  Only completed analyses can be exported
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters - Mobile Optimized */}
        <Card className="bg-gradient-to-br from-background via-background to-muted/20 border border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by symbol..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`pl-10 ${isMobile ? 'min-h-[44px]' : ''}`}
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={`w-full sm:w-[200px] ${isMobile ? 'min-h-[44px]' : ''}`}>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-background border shadow-lg">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="PROCESSING">Processing</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                  <SelectItem value="RATE_LIMITED">Rate Limited</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* History List - Mobile Optimized */}
        <Card className="bg-gradient-to-br from-background via-background to-muted/20 border border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
              Recent Analysis ({totalItems} total)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading analysis history...</p>
              </div>
            ) : paginatedSessions.length === 0 ? (
              <div className="text-center py-12">
                <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Analysis History</h3>
                <p className="text-muted-foreground mb-4 px-4">
                  {searchTerm || statusFilter !== 'all' ? 'No results match your filters.' : "You haven't run any market analysis yet."}
                </p>
                <Button onClick={() => navigate('/market-analysis')} size={isMobile ? "lg" : "default"}>
                  Start Your First Analysis
                </Button>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {paginatedSessions.map((session) => {
                  const action = getRecommendationAction(session);
                  const { date, time } = formatDate(session.created_at);
                  const isCompleted = session.status === 'COMPLETED';
                  
                  return (
                    <div
                      key={session.id}
                      className={`p-3 sm:p-4 bg-accent/30 rounded-lg border border-border/50 hover:bg-accent/50 transition-colors ${
                        isMobile ? 'space-y-3' : 'flex items-center justify-between'
                      }`}
                    >
                      {/* Mobile Layout */}
                      {isMobile ? (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {isCompleted && (
                                <input
                                  type="checkbox"
                                  checked={selectedSessions.includes(session.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedSessions([...selectedSessions, session.id]);
                                    } else {
                                      setSelectedSessions(selectedSessions.filter(id => id !== session.id));
                                    }
                                  }}
                                  className="w-4 h-4"
                                />
                              )}
                              
                              <div className="flex items-center gap-2">
                                {getActionIcon(action)}
                                <span className="font-semibold text-foreground">{session.symbol}</span>
                              </div>
                            </div>
                            
                            <div className="text-sm text-muted-foreground">
                              Progress: {session.progress_percentage}%
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={getActionColor(action)}>
                              {action}
                            </Badge>
                            <Badge className={getStatusColor(session.status)}>
                              {session.status}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {date} at {time}
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDetails(session.id)}
                              className="flex-1 flex items-center justify-center gap-1 min-h-[44px]"
                            >
                              <Eye className="h-3 w-3" />
                              View Details
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteSession(session.id)}
                              className="flex items-center justify-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 min-h-[44px] px-3"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        /* Desktop Layout */
                        <>
                          <div className="flex items-center gap-4">
                            {isCompleted && (
                              <input
                                type="checkbox"
                                checked={selectedSessions.includes(session.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedSessions([...selectedSessions, session.id]);
                                  } else {
                                    setSelectedSessions(selectedSessions.filter(id => id !== session.id));
                                  }
                                }}
                                className="w-4 h-4"
                              />
                            )}
                            
                            <div className="flex items-center gap-2">
                              {getActionIcon(action)}
                              <span className="font-semibold text-foreground">{session.symbol}</span>
                            </div>
                            
                            <Badge className={getActionColor(action)}>
                              {action}
                            </Badge>

                            <Badge className={getStatusColor(session.status)}>
                              {session.status}
                            </Badge>
                            
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {date} at {time}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <div className="text-sm text-muted-foreground">
                              Progress: {session.progress_percentage}%
                            </div>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDetails(session.id)}
                              className="flex items-center gap-1"
                            >
                              <Eye className="h-3 w-3" />
                              View Details
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteSession(session.id)}
                              className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination - Mobile Optimized */}
            {paginatedSessions.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setCurrentPage(1);
                }}
                className="mt-6"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MarketAnalysisHistoryPage;
